import { FC, FormEvent, MouseEvent } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import ValidationMessage from "@/components/Forms/ValidationMessage";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";
import { Dispatch, RootState } from "@/utils/store";
import Text from "@/components/Text";
import { selectTeam } from "@/selectors";
import Button from "@/components/Button";
import { FormLayout, Label, Larger, Textarea } from "@/components/Forms";

const Card = styled.form`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 24px 24px 40px;
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 32px;
`;

const Headline = styled(Text)`
  margin: 0 0 24px;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FaqFooter = styled.div`
  margin-top: 16px;
  text-align: right;
`;

const FaqField = styled(Larger)`
  position: relative;
`;

const RemoveButton = styled(Button)`
  position: absolute;
  top: -12px;
  right: 0;
  height: 27px;
  font-size: 11px;
`;

type FormDataT = {
  script_topics_note?: string;
  youtube_testing_faq: Array<{ text: string }>;
};

const Preferences: FC = () => {
  const { teamId } = useParams();

  const dispatch = useDispatch<Dispatch>();

  const preferences = useSelector(
    (rootState: RootState) => selectTeam(rootState, teamId!)?.preferences
  );

  const alerts = useAlerts();

  const methods = useForm<FormDataT>({
    defaultValues: {
      youtube_testing_faq: preferences?.youtube_testing_faq ?? [
        {
          text: "",
        },
      ],
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    control,
    clearErrors,
    formState: { errors, isSubmitting },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "youtube_testing_faq",
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await dispatch.teams.updateTeamPreferences({
          teamId: teamId!,
          preferences: data,
        });

        alerts.success(`Team preferences was successfully updated`);
      } catch (error) {
        handleFormErrors<FormDataT>(error, setError, alerts, true);
      }
    })(e);
  };

  const onAddFaqRow = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    append({ text: "" });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} autoComplete="off">
        <Grid>
          <Card>
            <Headline $type="caption" as="h2">
              Script topics note
            </Headline>
            <div>
              <Label htmlFor="descriptionInput">Description</Label>
              <Textarea
                {...register("script_topics_note")}
                $hasError={!!errors.script_topics_note}
                id="descriptionInput"
                defaultValue={preferences?.script_topics_note ?? ""}
                placeholder="Add description"
              />
              {!!errors.script_topics_note && (
                <ValidationMessage>
                  {errors.script_topics_note.message}
                </ValidationMessage>
              )}
            </div>
          </Card>
          <Card>
            <Headline $type="caption" as="h2">
              Youtube Testing FAQ
            </Headline>
            <FormLayout>
              {fields.map((field, index) => (
                <FaqField key={field.id}>
                  {index !== 0 && (
                    <RemoveButton
                      color="red"
                      small
                      onClick={() => remove(index)}
                      type="button"
                    >
                      Remove
                    </RemoveButton>
                  )}
                  <Label>Row #{index + 1}</Label>
                  <Textarea
                    {...register(`youtube_testing_faq.${index}.text`)}
                    $hasError={!!errors.youtube_testing_faq?.[index]?.text}
                    defaultValue=""
                    placeholder="Add text"
                  />
                  {!!errors.youtube_testing_faq?.[index]?.text && (
                    <ValidationMessage>
                      {
                        errors.youtube_testing_faq[index]!.text!
                          .message as string
                      }
                    </ValidationMessage>
                  )}
                </FaqField>
              ))}
            </FormLayout>
            <FaqFooter>
              <Button color="colorized" onClick={onAddFaqRow}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 2V14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 8H14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Add row
              </Button>
            </FaqFooter>
          </Card>
        </Grid>
        <Footer>
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
            Save
          </Button>
        </Footer>
      </form>
    </FormProvider>
  );
};

export default Preferences;
